import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { GamingReviewCustomer } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import ThresholdBadge from './ThresholdBadge'

const t = buildTranslate('recovery_ai.gaming_review')

export const SKELETON_ROW_COUNT = 5
// Keep in sync with the <th> count below — 3 data columns + 1 actions column.
const COLUMN_COUNT = 4

type Props = {
  rows: GamingReviewCustomer[]
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  // The pane owns the CASL gate + the Restrict modal; the table only signals intent.
  canManage: boolean
  onRestrict: (row: GamingReviewCustomer) => void
  // AI-303 Unit 7: the merchant's configured auto-resolution limit (from
  // getRecoveryConfig, supplied by the pane). Optional — absent until the nexus
  // companion ships, in which case the badge shows the count only.
  autoResolutionThreshold?: number
}

// Presentational only — no data fetching, no dispatch, no ability hook. Copies
// the RestrictedCustomersTable STRUCTURE (header cells, skeleton, empty,
// error/retry). Per-row action is a single Restrict Button (Restrict-only, no
// whitelist/menu). Restrict is shown only to managers and is DISABLED for a row
// with no source conversation (RestrictCustomerModal POSTs by conversation_id).
const GamingReviewTable = ({
  rows,
  isLoading = false,
  isError = false,
  onRetry,
  canManage,
  onRestrict,
  autoResolutionThreshold,
}: Props) => {
  const [css] = useStyletron()

  const headerCellClass = css({
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    borderBottom: `2px solid ${merchantTheme.colors.grey20}`,
    textAlign: 'left',
  })

  const cellClass = css({
    padding: '16px',
    fontSize: '14px',
    color: merchantTheme.colors.grey70,
    borderBottom: `1px solid ${merchantTheme.colors.grey10}`,
  })

  if (isError) {
    return (
      <div
        className={css({
          padding: '48px 0',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        <Text.BodyText3 color="grey70" className="mb-s">
          {t('error.body')}
        </Text.BodyText3>
        {onRetry && (
          <Button kind="secondary" onClick={onRetry}>
            {t('error.retry')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <table className={css({ width: '100%', borderCollapse: 'collapse' })}>
      <caption className="sr-only">{t('table.caption')}</caption>
      <thead>
        <tr>
          <th scope="col" className={headerCellClass}>
            {t('table.customer')}
          </th>
          <th scope="col" className={headerCellClass}>
            {t('table.auto_resolutions')}
          </th>
          <th scope="col" className={headerCellClass}>
            {t('table.period')}
          </th>
          <th scope="col" className={headerCellClass}>
            <span className="sr-only">{t('table.actions')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
            <tr
              key={`skeleton-row-${i}`}
              data-testid="gaming-review-skeleton-row"
              aria-hidden="true"
            >
              {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
                <td key={`skeleton-cell-${i}-${j}`} className={cellClass}>
                  <div
                    className={css({
                      height: '14px',
                      borderRadius: '4px',
                      backgroundColor: merchantTheme.colors.grey20,
                    })}
                  />
                </td>
              ))}
            </tr>
          ))
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={COLUMN_COUNT}>
              <div className={css({ padding: '48px 0', textAlign: 'center' })}>
                <Text.BodyText3 color="grey50">{t('empty')}</Text.BodyText3>
              </div>
            </td>
          </tr>
        ) : (
          rows.map(row => {
            const canRestrictRow = row.conversationId !== null
            return (
              <tr key={row.userId}>
                <td className={cellClass}>
                  <div className="d-flex flex-column">
                    <Text.BodyText4 color="grey90">
                      {row.customerName}
                    </Text.BodyText4>
                    {row.storeName && (
                      <Text.BodyText5 color="grey50">
                        {row.storeName}
                      </Text.BodyText5>
                    )}
                  </div>
                </td>
                <td className={cellClass}>
                  <ThresholdBadge
                    count={row.autoResolutionCount}
                    threshold={autoResolutionThreshold}
                  />
                </td>
                <td className={cellClass}>
                  {t('period_window', { days: row.periodDays })}
                </td>
                <td className={cellClass}>
                  {canManage && (
                    <Button
                      kind="secondary"
                      onClick={() => onRestrict(row)}
                      disabled={!canRestrictRow}
                      aria-label={t(
                        canRestrictRow
                          ? 'restrict.action_aria'
                          : 'restrict.unavailable_aria',
                        { name: row.customerName }
                      )}
                    >
                      {t('restrict.button')}
                    </Button>
                  )}
                </td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )
}

export default GamingReviewTable
